import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { fabric } from 'fabric';

const CanvasContainer = styled(motion.div)`
  background: rgba(34, 197, 94, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 30px;
  border: 1px solid rgba(34, 197, 94, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
`;

const CanvasTitle = styled.h3`
  color: #22c55e;
  margin-bottom: 20px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CanvasWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  position: relative;
  width: 100%;
  max-width: 600px;
  aspect-ratio: 1;
  margin: 0 auto 20px auto;
  border: 3px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  background: transparent;
  overflow: hidden;
  transition: all 0.3s ease;
  will-change: auto;
  transform: translateZ(0);
  
  &:hover {
    border-color: rgba(34, 197, 94, 0.5);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4), 0 0 20px rgba(34, 197, 94, 0.2);
    transform: translateY(-2px) translateZ(0);
  }
  
  @media (max-width: 1023px) {
    max-width: 500px;
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  background: rgba(239, 68, 68, 0.9);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(220, 38, 38, 0.9);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  }
`;

const Canvas = styled.canvas`
  border: 3px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  max-width: 100%;
  height: auto;
  background: transparent;
  display: block !important;
  position: relative !important;
`;

const DropZone = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 3px dashed rgba(34, 197, 94, 0.5);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => props.$isVisible ? 1 : 0};
  pointer-events: ${props => props.$isVisible ? 'auto' : 'none'};
  z-index: 50;

  &:hover {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.08) 100%);
    border-color: rgba(34, 197, 94, 0.8);
    transform: scale(1.02);
  }

  &.drag-over {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%);
    border-color: #22c55e;
    transform: scale(1.05);
  }
`;

const DropZoneIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 15px;
  color: #22c55e;
`;

const DropZoneText = styled.div`
  color: #22c55e;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 10px;
`;

const DropZoneSubtext = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  text-align: center;
`;

const ControlsContainer = styled.div`
  background: rgba(34, 197, 94, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 25px;
  border: 1px solid rgba(34, 197, 94, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const MainContent = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 1023px) {
    flex-direction: column;
    align-items: center;
    padding: 0 10px;
  }
`;

const CanvasSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const ToolsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* width: 170px; */
  padding: 12px;
  background: linear-gradient(135deg, rgba(15, 15, 15, 0.95), rgba(25, 25, 25, 0.95));
  border: 2px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  backdrop-filter: blur(15px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
  transition: all 0.3s ease;
  will-change: auto;
  transform: translateZ(0);
  
  &:hover {
    border-color: rgba(34, 197, 94, 0.5);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(34, 197, 94, 0.1);
    transform: translateY(-2px) translateZ(0);
  }
  
  @media (max-width: 1023px) {
    width: 100%;
    max-width: 500px;
    margin-top: 20px;
  }
`;

const ToolGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ToolTitle = styled.h3`
  color: #22c55e;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ToolButton = styled(motion.button)`
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.1));
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  color: #22c55e;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2));
    border-color: rgba(34, 197, 94, 0.5);
    transform: translateY(-1px);
  }
  
  &.danger {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1));
    border-color: rgba(239, 68, 68, 0.3);
    color: #ef4444;
    
    &:hover {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2));
      border-color: rgba(239, 68, 68, 0.5);
    }
  }
`;

const ToolRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const SaveButton = styled(motion.button)`
  background: linear-gradient(45deg, #22c55e, #16a34a);
  border: none;
  color: white;
  padding: 10px 5px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.4s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s ease;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    background: linear-gradient(45deg, #16a34a, #15803d);
    box-shadow: 0 12px 30px rgba(34, 197, 94, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(10px);
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, rgba(15, 15, 15, 0.95), rgba(25, 25, 25, 0.95));
  border: 2px solid rgba(34, 197, 94, 0.3);
  border-radius: 16px;
  padding: 30px;
  width: 90vw;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  
  @media (max-width: 768px) {
    width: 95vw;
    padding: 20px;
  }
  
  @media (max-width: 480px) {
    width: 98vw;
    padding: 15px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  color: #22c55e;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseModalButton = styled(motion.button)`
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 1.2rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.3);
    transform: scale(1.1);
  }
`;

const StickerModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 8px;
  margin-bottom: 25px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
    gap: 10px;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 12px;
  }
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: 15px;
  }
`;

const StickerModalButton = styled(motion.button)`
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.1));
  border: 2px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  padding: 8px;
  color: #22c55e;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  aspect-ratio: 1;
  
  @media (min-width: 768px) {
    padding: 10px;
    font-size: 1.4rem;
    min-height: 60px;
  }
  
  @media (min-width: 1024px) {
    padding: 12px;
    font-size: 1.6rem;
    min-height: 70px;
  }
  
  @media (min-width: 1200px) {
    padding: 14px;
    font-size: 1.8rem;
    min-height: 80px;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2));
    border-color: rgba(34, 197, 94, 0.5);
    transform: translateY(-2px);
  }
`;

const StickerModalLabel = styled.span`
  font-size: 0.6rem;
  margin-top: 4px;
  text-align: center;
  line-height: 1;
  
  @media (min-width: 768px) {
    font-size: 0.7rem;
    margin-top: 5px;
  }
  
  @media (min-width: 1024px) {
    font-size: 0.8rem;
    margin-top: 6px;
  }
  
  @media (min-width: 1200px) {
    font-size: 0.9rem;
    margin-top: 8px;
  }
`;

const ModalSection = styled.div`
  margin-bottom: 25px;
`;

const SectionTitle = styled.h3`
  color: #22c55e;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 15px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ControlRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 25px;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 0;
    margin-bottom: 0;
  }
`;

const ControlColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ControlGroup = styled.div`
  margin-bottom: 25px;
`;

const ControlTitle = styled.h4`
  color: #22c55e;
  margin-bottom: 15px;
  font-size: 1.2rem;
  font-weight: 600;
`;

const Button = styled(motion.button)`
  background: #22c55e;
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin: 5px;
  transition: all 0.3s ease;
  width: 100%;
  font-size: 0.95rem;

  &:hover {
    transform: translateY(-2px);
    background: #16a34a;
    box-shadow: 0 8px 20px rgba(34, 197, 94, 0.3);
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }

  &.danger {
    background: #ef4444;
    
    &:hover {
      background: #dc2626;
      box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
    }
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled(motion.label)`
  display: block;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  margin: 5px 0;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }
`;


const ColorInput = styled.input`
  width: 50px;
  height: 40px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  margin: 8px 0;
  background: transparent;
  flex-shrink: 0;
`;

const InputRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 8px 0;
`;

const TextInput = styled.input`
  flex: 1;
  padding: 12px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  backdrop-filter: blur(10px);

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  margin: 10px 0;
`;

const StickerPanel = styled(motion.div)`
  background: rgba(34, 197, 94, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 20px;
  margin-top: 15px;
  border: 1px solid rgba(34, 197, 94, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const StickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 15px;
`;

const StickerButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(34, 197, 94, 0.2);
    border-color: #22c55e;
    transform: scale(1.05);
  }
`;

const CustomUploadLabel = styled.label`
  display: block;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  text-align: center;
  font-size: 0.9rem;
  color: white;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(34, 197, 94, 0.2);
    border-color: #22c55e;
  }
`;

const StickerInput = styled.input`
  display: none;
`;

function CanvasEditor() {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [textColor, setTextColor] = useState('#ff6b6b');
  const [showDropZone, setShowDropZone] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showStickerPanel, setShowStickerPanel] = useState(false);
  const [lastText, setLastText] = useState('');
  const [currentTextObject, setCurrentTextObject] = useState(null);
  const [showStickerModal, setShowStickerModal] = useState(false);

  useEffect(() => {
    console.log('useEffect 开始执行');
    const canvasElement = canvasRef.current;
    console.log('Canvas 元素引用:', canvasElement);
    
    if (!canvasElement) {
      console.log('Canvas 元素不存在，退出');
      return;
    }
    
    // 检查是否已经初始化过
    if (canvas) {
      console.log('Canvas 已经初始化，跳过');
      return;
    }
    
    // 检查是否已经有 fabric canvas 实例
    if (canvasElement.fabric) {
      console.log('Canvas 元素已经有 fabric 实例，跳过');
      return;
    }
    
    // 添加一个标志来防止重复初始化
    if (canvasElement.dataset.initialized === 'true') {
      console.log('Canvas 已经标记为初始化，跳过');
      return;
    }
    
    // 标记为已初始化
    canvasElement.dataset.initialized = 'true';
    
    // 延迟初始化，确保DOM完全渲染
    setTimeout(() => {
      console.log('开始初始化 fabric.js');
      
      // 清除所有可能的样式干扰
      canvasElement.style.cssText = `
        border: 3px solid rgba(34, 197, 94, 0.3);
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        max-width: 100%;
        height: auto;
        background: transparent;
        display: block !important;
        position: relative !important;
      `;
      
      // 首先测试原生 canvas 2D 上下文
      const ctx = canvasElement.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'blue';
        ctx.fillRect(10, 10, 50, 50);
        ctx.fillStyle = 'green';
        ctx.fillRect(70, 10, 50, 50);
        ctx.fillStyle = 'red';
        ctx.fillRect(130, 10, 50, 50);
        console.log('原生 canvas 2D 测试完成');
      } else {
        console.error('无法获取 canvas 2D 上下文');
      }
      
      // 使用 fabric.Canvas 但确保正确的配置
      const fabricCanvas = new fabric.Canvas(canvasElement, {
        width: 600,
        height: 600,
        backgroundColor: 'transparent',
        renderOnAddRemove: true,
        skipTargetFind: false,
        selectable: true,
        preserveObjectStacking: true,
        allowTouchScrolling: false,
        fireRightClick: true,
        stopContextMenu: false,
        imageSmoothingEnabled: true,
        enablePointerEvents: true,
        uniformScaling: false
      });
      
      // 确保 upper canvas 也正确设置
      const upperCanvas = fabricCanvas.upperCanvasEl;
      console.log('Upper canvas 元素:', upperCanvas);
      if (upperCanvas) {
        upperCanvas.style.position = 'absolute';
        upperCanvas.style.left = '0px';
        upperCanvas.style.top = '0px';
        upperCanvas.style.width = '600px';
        upperCanvas.style.height = '600px';
        upperCanvas.style.zIndex = '10';
        upperCanvas.style.display = 'block';
        upperCanvas.style.visibility = 'visible';
        upperCanvas.style.opacity = '1';
        upperCanvas.style.background = 'transparent';
        upperCanvas.style.border = 'none';
        upperCanvas.style.borderRadius = '0';
        upperCanvas.style.boxShadow = 'none';
        upperCanvas.style.pointerEvents = 'auto';
        // 禁用 upper canvas 的渲染，让所有内容都在 lower canvas 上
        upperCanvas.style.display = 'none';
        console.log('Upper canvas 样式已设置（已禁用）');
      } else {
        console.error('无法找到 upper canvas 元素');
      }
      
      // 确保 lower canvas 也正确设置
      const lowerCanvas = fabricCanvas.lowerCanvasEl;
      console.log('Lower canvas 元素:', lowerCanvas);
      if (lowerCanvas) {
        lowerCanvas.style.position = 'absolute';
        lowerCanvas.style.left = '0px';
        lowerCanvas.style.top = '0px';
        lowerCanvas.style.width = '600px';
        lowerCanvas.style.height = '600px';
        lowerCanvas.style.zIndex = '1';
        lowerCanvas.style.display = 'block';
        lowerCanvas.style.visibility = 'visible';
        lowerCanvas.style.opacity = '1';
        lowerCanvas.style.background = 'transparent';
        lowerCanvas.style.border = 'none';
        lowerCanvas.style.borderRadius = '0';
        lowerCanvas.style.boxShadow = 'none';
        lowerCanvas.style.pointerEvents = 'auto';
        console.log('Lower canvas 样式已设置');
      } else {
        console.error('无法找到 lower canvas 元素');
      }

      // 确保画布正确初始化
      fabricCanvas.renderAll();
      
      // 添加事件监听器来调试对象选择
      fabricCanvas.on('selection:created', (e) => {
        console.log('selection:created - 对象被选中:', e.selected);
        if (e.selected && e.selected.length > 0) {
          handleObjectSelection(e.selected[0]);
        }
      });
      
      fabricCanvas.on('selection:updated', (e) => {
        console.log('selection:updated - 选择更新:', e.selected);
        console.log('selection:updated - 选择对象数量:', e.selected ? e.selected.length : 0);
        if (e.selected && e.selected.length > 0) {
          console.log('selection:updated - 处理选中对象:', e.selected[0].text);
          handleObjectSelection(e.selected[0]);
        } else {
          console.log('selection:updated - 没有选中对象，调用取消选择');
          handleObjectDeselection();
        }
      });
      
      fabricCanvas.on('selection:cleared', () => {
        console.log('selection:cleared - 选择被清除');
        handleObjectDeselection();
      });
      
      fabricCanvas.on('object:moving', (e) => {
        console.log('对象正在移动:', e.target);
      });
      
      fabricCanvas.on('object:scaling', (e) => {
        console.log('对象正在缩放:', e.target);
      });
      
      // 强制设置 canvas 容器的样式
      const canvasContainer = canvasElement.parentElement;
      if (canvasContainer) {
        canvasContainer.style.position = 'relative';
        canvasContainer.style.width = '600px';
        canvasContainer.style.height = '600px';
        canvasContainer.style.overflow = 'hidden';
        canvasContainer.style.border = '3px solid rgba(34, 197, 94, 0.3)';
        canvasContainer.style.borderRadius = '12px';
        canvasContainer.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.3)';
        canvasContainer.style.backgroundColor = 'transparent';
        console.log('Canvas 容器样式已设置');
        console.log('Canvas 容器尺寸:', canvasContainer.offsetWidth, 'x', canvasContainer.offsetHeight);
      }
      
      // 添加调试信息
      console.log('Fabric Canvas 初始化完成');
      console.log('Canvas 元素:', canvasElement);
      console.log('Canvas 尺寸:', fabricCanvas.width, 'x', fabricCanvas.height);
      console.log('Canvas 容器:', canvasContainer);
      
      
      // 多次尝试渲染
      fabricCanvas.renderAll();
      setTimeout(() => fabricCanvas.renderAll(), 50);
      setTimeout(() => fabricCanvas.renderAll(), 100);
      
      console.log('画布对象数量:', fabricCanvas.getObjects().length);
      
      // 强制重新设置 canvas 尺寸和位置
      setTimeout(() => {
        fabricCanvas.setWidth(600);
        fabricCanvas.setHeight(600);
        fabricCanvas.renderAll();
        console.log('强制重新设置尺寸完成');
        
        // 强制重新设置所有 canvas 元素的样式
        const allCanvases = canvasContainer.querySelectorAll('canvas');
        console.log('找到的 canvas 元素数量:', allCanvases.length);
        allCanvases.forEach((canvasEl, index) => {
          canvasEl.style.display = 'block';
          canvasEl.style.visibility = 'visible';
          canvasEl.style.opacity = '1';
          canvasEl.style.position = 'absolute';
          canvasEl.style.left = '0px';
          canvasEl.style.top = '0px';
          canvasEl.style.width = '600px';
          canvasEl.style.height = '600px';
          canvasEl.style.backgroundColor = 'transparent';
          canvasEl.style.background = 'transparent';
          canvasEl.style.border = 'none';
          canvasEl.style.borderRadius = '0';
          canvasEl.style.boxShadow = 'none';
          canvasEl.style.zIndex = index === 0 ? '1' : '10';
          console.log(`Canvas ${index} 样式已强制设置`);
          console.log(`Canvas ${index} 位置:`, canvasEl.offsetLeft, canvasEl.offsetTop);
          console.log(`Canvas ${index} 尺寸:`, canvasEl.offsetWidth, 'x', canvasEl.offsetHeight);
        });
      }, 100);
      
      // 检查画布是否真的渲染了
      setTimeout(() => {
        console.log('延迟检查 - 画布对象数量:', fabricCanvas.getObjects().length);
        console.log('画布是否为空:', fabricCanvas.isEmpty());
        console.log('画布背景色:', fabricCanvas.backgroundColor);
        
        // 尝试强制重新渲染
        fabricCanvas.renderAll();
        console.log('强制重新渲染完成');
      }, 200);
      
      setCanvas(fabricCanvas);
    }, 100);

    return () => {
      if (canvas) {
        canvas.dispose();
      }
    };
  }, []); // 空依赖数组，确保只执行一次

  const handleFileUpload = (file) => {
    if (file && file.type.startsWith('image/') && canvas) {
      console.log('开始上传图片:', file.name, file.type);
      console.log('当前画布对象数量:', canvas.getObjects().length);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('文件读取完成，开始创建图片');
        const img = new Image();
        img.onload = () => {
          console.log('图片加载完成，尺寸:', img.width, 'x', img.height);

          // 使用 fabric.Image.fromURL 方法
          fabric.Image.fromURL(e.target.result, (fabricImg) => {
            if (!fabricImg) {
              console.error('无法创建 fabric 图片对象');
              return;
            }
            
            // 计算缩放比例，让图片高度铺满canvas
            const scale = canvas.height / img.height;
            fabricImg.set({
              scaleX: scale,
              scaleY: scale,
              left: canvas.width / 2 - (img.width * scale) / 2,
              top: 0,
              selectable: true,
              evented: true,
              moveable: true,
              borderColor: '#22c55e',
              cornerColor: '#22c55e',
              cornerSize: 10,
              transparentCorners: false,
              hasControls: true,
              hasBorders: true,
              lockMovementX: false,
              lockMovementY: false,
              lockRotation: false,
              lockScalingX: false,
              lockScalingY: false
            });
            
            console.log('添加图片到画布');
            canvas.add(fabricImg);
            canvas.setActiveObject(fabricImg);

            // 调试：检查画布对象
            console.log('画布对象数量:', canvas.getObjects().length);
            console.log('画布尺寸:', canvas.width, 'x', canvas.height);
            console.log('画布背景色:', canvas.backgroundColor);
            console.log('添加的图片对象:', fabricImg);

            // 强制渲染
            canvas.renderAll();
            
            // 多次尝试渲染，确保图片显示
            setTimeout(() => canvas.renderAll(), 50);
            setTimeout(() => canvas.renderAll(), 100);

            // 延迟添加logo贴纸，确保主图片先渲染
            setTimeout(() => {
              addLogoSticker();
            }, 200);

            setShowDropZone(false);
            console.log('图片上传完成');
          });
        };
        img.onerror = (error) => {
          console.error('图片加载失败:', error);
        };
        img.src = e.target.result;
      };
      reader.onerror = (error) => {
        console.error('文件读取失败:', error);
      };
      reader.readAsDataURL(file);
    } else {
      console.log('不支持的文件类型或canvas未初始化:', file?.type, !!canvas);
    }
  };

  const addLogoSticker = () => {
    if (!canvas) {
      console.log('Canvas未初始化，无法添加logo');
      return;
    }

    console.log('开始加载logo贴纸');
    const logoImg = new Image();
    logoImg.onload = () => {
      console.log('Logo加载完成，尺寸:', logoImg.width, 'x', logoImg.height);
      const scale = 80 / logoImg.width;
      const fabricLogo = new fabric.Image(logoImg, {
        scaleX: scale,
        scaleY: scale,
        left: canvas.width - (logoImg.width * scale) - 40,
        top: 40,
        selectable: true,
        evented: true,
        moveable: true,
        borderColor: '#22c55e',
        cornerColor: '#22c55e',
        cornerSize: 8,
        transparentCorners: false,
        hasControls: true,
        hasBorders: true,
        lockMovementX: false,
        lockMovementY: false,
        lockRotation: false,
        lockScalingX: false,
        lockScalingY: false
      });
      canvas.add(fabricLogo);
      canvas.setActiveObject(fabricLogo);
      canvas.renderAll();
      console.log('Logo贴纸添加完成');
      console.log('添加logo后画布对象数量:', canvas.getObjects().length);
    };
    logoImg.onerror = (error) => {
      console.error('Logo加载失败:', error);
      // 如果logo加载失败，添加一个emoji作为替代
      const fallbackSticker = new fabric.Text('🚀', {
        left: canvas.width - 100,
        top: 20,
        fontSize: 60,
        selectable: true,
        borderColor: '#22c55e',
        cornerColor: '#22c55e',
        cornerSize: 8,
        transparentCorners: false
      });
      canvas.add(fallbackSticker);
      canvas.setActiveObject(fallbackSticker);
      canvas.renderAll();
      console.log('Fallback贴纸添加完成');
      console.log('添加fallback后画布对象数量:', canvas.getObjects().length);
    };
    logoImg.src = '/logo.png';
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    console.log('文件输入变化:', file?.name, file?.type);
    if (file) {
      handleFileUpload(file);
    }
    // 清空 input 值，允许重复选择同一文件
    event.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDropZoneClick = () => {
    console.log('DropZone 被点击');
    console.log('当前 canvas 状态:', !!canvas);
    console.log('当前画布对象数量:', canvas?.getObjects().length);
    fileInputRef.current?.click();
  };

  const addSticker = (sticker) => {
    if (!canvas) {
      console.log('Canvas未初始化，无法添加贴纸');
      return;
    }
    
    if (sticker === 'logo') {
      // 添加logo贴纸
      addLogoSticker();
    } else {
      // 添加emoji贴纸
      const textSticker = new fabric.Text(sticker, {
        left: Math.random() * (canvas.width - 100) + 50,
        top: Math.random() * (canvas.height - 100) + 50,
        fontSize: 60,
        selectable: true,
        evented: true,
        moveable: true,
        borderColor: '#22c55e',
        cornerColor: '#22c55e',
        cornerSize: 10,
        transparentCorners: false,
        hasControls: true,
        hasBorders: true,
        lockMovementX: false,
        lockMovementY: false,
        lockRotation: false,
        lockScalingX: false,
        lockScalingY: false
      });
      canvas.add(textSticker);
      canvas.setActiveObject(textSticker);
      canvas.renderAll();
      console.log('贴纸添加完成:', sticker);
    }
  };

  const addCustomSticker = (file) => {
    if (file && file.type.startsWith('image/') && canvas) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
            const fabricImg = new fabric.Image(img, {
              scaleX: 80 / img.width,
              scaleY: 80 / img.width,
              left: Math.random() * (canvas.width - 100) + 20,
              top: Math.random() * (canvas.height - 100) + 20,
              selectable: true,
              evented: true,
              moveable: true,
              borderColor: '#22c55e',
              cornerColor: '#22c55e',
              cornerSize: 8,
              transparentCorners: false,
              hasControls: true,
              hasBorders: true,
              lockMovementX: false,
              lockMovementY: false,
              lockRotation: false,
              lockScalingX: false,
              lockScalingY: false
            });
          canvas.add(fabricImg);
          canvas.setActiveObject(fabricImg);
          canvas.renderAll();
          console.log('自定义贴纸添加完成');
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const emojiStickers = ['🎩', '👑', '💎', '⭐', '🔥', '💯', '🚀', '💰', '🎯', '💪', '🎨', '✨'];
  const xwfStickers = ['logo'];

  const addText = (inputText, color) => {
    if (inputText && inputText.trim() && canvas) {
      // 只有在有当前文字对象时才更新，否则创建新的
      if (currentTextObject) {
        currentTextObject.set('text', inputText);
        currentTextObject.set('fill', color || textColor);
        canvas.renderAll();
        console.log('更新当前文字对象:', inputText);
      } else {
        // 创建新的文字对象
        const text = new fabric.Text(inputText, {
          left: Math.random() * (canvas.width - 200) + 50,
          top: Math.random() * (canvas.height - 100) + 50,
          fontSize: 30,
          fill: color || textColor,
          fontFamily: 'Arial',
          fontWeight: 'bold',
          selectable: true,
          evented: true,
          moveable: true,
          borderColor: '#22c55e',
          cornerColor: '#22c55e',
          cornerSize: 10,
          transparentCorners: false,
          hasControls: true,
          hasBorders: true,
          lockMovementX: false,
          lockMovementY: false,
          lockRotation: false,
          lockScalingX: false,
          lockScalingY: false
        });
        canvas.add(text);
        canvas.setActiveObject(text);
        setCurrentTextObject(text);
        canvas.renderAll();
        console.log('创建新文字对象:', inputText);
      }
    } else if (inputText === '' && currentTextObject) {
      // 如果输入为空且有当前文字对象，删除它
      canvas.remove(currentTextObject);
      setCurrentTextObject(null);
      canvas.renderAll();
      console.log('文字已删除');
    }
  };

  const handleTextInputFocus = () => {
    // 当输入框获得焦点时，如果没有当前文字对象，创建一个新的
    if (!currentTextObject) {
      console.log('输入框获得焦点，没有当前文字对象，准备创建新文字');
    }
  };

  const handleSaveImage = () => {
    if (canvas) {
      const dataURL = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'xwf-avatar.png';
      link.href = dataURL;
      link.click();
      console.log('XWF头像已保存');
    }
  };

  const handleTextInputChange = (e) => {
    const value = e.target.value;
    setTextInput(value);
    
    // 只有在有当前文字对象时才更新，否则不创建新对象
    if (currentTextObject) {
      addText(value, textColor);
    } else if (value && value.trim()) {
      // 如果没有当前文字对象但有输入内容，创建新文字对象
      addText(value, textColor);
    }
  };

  const handleColorChange = (e) => {
    const color = e.target.value;
    setTextColor(color);
    
    // 只有在有当前文字对象时才更新，否则不创建新对象
    if (currentTextObject) {
      addText(textInput, color);
    }
  };

  const handleTextInputBlur = () => {
    // 当输入框失去焦点时，延迟检查是否有新的文字对象被选中
    setTimeout(() => {
      // 检查当前是否有选中的文字对象
      const activeObject = canvas.getActiveObject();
      if (activeObject && activeObject.type === 'text') {
        // 如果有选中的文字对象，回填其内容
        console.log('输入框失去焦点，但有文字对象被选中，回填内容:', activeObject.text);
        setTextInput(activeObject.text);
        setTextColor(activeObject.fill);
        setCurrentTextObject(activeObject);
      } else {
        // 如果没有选中的文字对象，清空输入框
        console.log('输入框失去焦点，没有文字对象被选中，清空输入框');
        setTextInput('');
        setCurrentTextObject(null);
      }
    }, 100); // 延迟100ms，让选择事件先触发
  };

  const handleObjectSelection = (selectedObject) => {
    if (selectedObject && selectedObject.type === 'text') {
      // 如果选中的是文字对象，回填到输入框
      console.log('选中文字对象:', selectedObject.text, '当前输入框内容:', textInput);
      
      // 强制更新输入框内容
      const textContent = selectedObject.text || '';
      const textFill = selectedObject.fill || textColor;
      
      setTextInput(textContent);
      setTextColor(textFill);
      setCurrentTextObject(selectedObject);
      
      console.log('文字对象被选中，回填到输入框:', textContent);
    } else {
      // 如果选中的不是文字对象，清除当前文字对象引用
      setCurrentTextObject(null);
      console.log('选中非文字对象，清除当前文字对象引用');
    }
  };

  const handleObjectDeselection = () => {
    // 当取消选择时，清除当前文字对象引用
    setCurrentTextObject(null);
    console.log('取消选择，清除当前文字对象引用');
  };

  const deleteSelected = () => {
    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length > 0) {
      activeObjects.forEach(obj => canvas.remove(obj));
      canvas.discardActiveObject();
      canvas.renderAll();
    }
  };

  const rotateSelected = (angle) => {
    if (canvas) {
      const activeObject = canvas.getActiveObject();
      if (activeObject) {
        activeObject.rotate(activeObject.angle + angle);
        canvas.renderAll();
        console.log(`对象已旋转 ${angle} 度，当前角度: ${activeObject.angle}`);
      } else {
        console.log('没有选中的对象');
      }
    }
  };

  const resetRotation = () => {
    if (canvas) {
      const activeObject = canvas.getActiveObject();
      if (activeObject) {
        activeObject.rotate(0);
        canvas.renderAll();
        console.log('对象旋转已重置');
      } else {
        console.log('没有选中的对象');
      }
    }
  };

  const bringToFront = () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.bringToFront(activeObject);
      canvas.renderAll();
    }
  };

  const sendToBack = () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.sendToBack(activeObject);
      canvas.renderAll();
    }
  };

  const downloadImage = () => {
    const dataURL = canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 2
    });
    
    const link = document.createElement('a');
    link.download = 'token-avatar.png';
    link.href = dataURL;
    link.click();
  };

  return (
    <CanvasContainer
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.6 }}
    >
      {/* <CanvasTitle>🎨 头像编辑器</CanvasTitle>   */}
      
      <MainContent>
        <CanvasSection>
          <CanvasWrapper>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <canvas
                ref={canvasRef} 
                style={{
                  width: '100%',
                  height: '100%',
                  background: 'transparent',
                  display: 'block',
                  position: 'relative',
                  maxWidth: '100%',
                  maxHeight: '100%'
                }}
              />
              {!showDropZone && (
                <CloseButton
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    setShowDropZone(true);
                    if (canvas) {
                      canvas.clear();
                      canvas.setBackgroundColor('transparent', canvas.renderAll.bind(canvas));
                      canvas.renderAll();
                      console.log('Canvas 已清空并重新渲染');
                    }
                  }}
                >
                  ×
                </CloseButton>
              )}
            </div>
            <DropZone
              $isVisible={showDropZone}
              className={isDragOver ? 'drag-over' : ''}
              onClick={handleDropZoneClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <DropZoneIcon>📸</DropZoneIcon>
              <DropZoneText>点击或拖拽上传头像</DropZoneText>
              <DropZoneSubtext>支持 JPG、PNG、GIF 格式</DropZoneSubtext>
            </DropZone>
            <FileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
            />
          </CanvasWrapper>
        </CanvasSection>

        <ToolsPanel>
          <ToolGroup>
            <ToolTitle>🛠️ 编辑工具</ToolTitle>
            <ToolRow>
              <ToolButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={bringToFront}
              >
                ⬆️ 置顶
              </ToolButton>
              <ToolButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={sendToBack}
              >
                ⬇️ 置底
              </ToolButton>
            </ToolRow>
            
            <ToolTitle style={{ marginTop: '15px', marginBottom: '8px' }}>🔄 旋转工具</ToolTitle>
            <ToolRow>
              <ToolButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => rotateSelected(-90)}
                style={{ fontSize: '0.8rem' }}
              >
                ↶ -90°
              </ToolButton>
              <ToolButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => rotateSelected(90)}
                style={{ fontSize: '0.8rem' }}
              >
                ↷ +90°
              </ToolButton>
            </ToolRow>
            <ToolRow>
              <ToolButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => rotateSelected(-45)}
                style={{ fontSize: '0.8rem' }}
              >
                ↶ -45°
              </ToolButton>
              <ToolButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => rotateSelected(45)}
                style={{ fontSize: '0.8rem' }}
              >
                ↷ +45°
              </ToolButton>
            </ToolRow>
            <ToolButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetRotation}
              style={{ fontSize: '0.8rem' }}
            >
              🔄 重置旋转
            </ToolButton>
            
            <ToolTitle style={{ marginTop: '15px', marginBottom: '8px' }}>✏️ 文字工具</ToolTitle>
            <div style={{ width: '100%' }}>
              <input
                type="text"
                placeholder="输入文字..."
                value={textInput}
                onChange={handleTextInputChange}
                onFocus={handleTextInputFocus}
                onBlur={handleTextInputBlur}
                style={{
                  width: 'calc(100% - 48px)',
                  padding: '8px 12px',
                  background: 'rgba(15, 15, 15, 0.8)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '6px',
                  color: '#22c55e',
                  fontSize: '0.85rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  minHeight: '36px',
                  boxSizing: 'border-box',
                  marginRight: '8px',
                  display: 'inline-block'
                }}
              />
              <input
                type="color"
                value={textColor}
                onChange={handleColorChange}
                style={{
                  width: '40px',
                  height: '36px',
                  background: 'transparent',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  outline: 'none',
                  boxSizing: 'border-box',
                  display: 'inline-block',
                  verticalAlign: 'top'
                }}
              />
            </div>
            
            <ToolButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowStickerModal(true)}
              style={{ marginTop: '10px' }}
            >
              🎨 贴纸工具
            </ToolButton>
            
            <ToolButton
              className="danger"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={deleteSelected}
              style={{ marginTop: '8px' }}
            >
              🗑️ 删除选中
            </ToolButton>
          </ToolGroup>
          
          <ToolGroup>
            {/* <ToolTitle>💾 保存图片</ToolTitle> */}
            <ToolRow>
              <SaveButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSaveImage}
                style={{ marginTop: '0' }}
              >
                💾 保存分享
              </SaveButton>
            </ToolRow>
          </ToolGroup>
        </ToolsPanel>
      </MainContent>

      {/* 贴纸工具Modal */}
      {showStickerModal && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowStickerModal(false)}
        >
          <ModalContent
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>🎨 贴纸工具</ModalTitle>
              <CloseModalButton
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowStickerModal(false)}
              >
                ×
              </CloseModalButton>
            </ModalHeader>

            <ModalSection>
              <SectionTitle>XWF 贴纸</SectionTitle>
              <StickerModalGrid>
                {xwfStickers.map((sticker, index) => (
                  <StickerModalButton
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      addSticker(sticker);
                      setShowStickerModal(false);
                    }}
                  >
                    {sticker === 'logo' ? (
                      <img 
                        src="/logo.png" 
                        alt="XWF Logo" 
                        style={{ 
                          width: '24px', 
                          height: '24px',
                          objectFit: 'contain'
                        }} 
                      />
                    ) : sticker}
                    <StickerModalLabel>
                      {sticker === 'logo' ? 'XWF Logo' : sticker}
                    </StickerModalLabel>
                  </StickerModalButton>
                ))}
              </StickerModalGrid>
            </ModalSection>

            <ModalSection>
              <SectionTitle>Emoji 贴纸</SectionTitle>
              <StickerModalGrid>
                {emojiStickers.map((emoji, index) => (
                  <StickerModalButton
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      addSticker(emoji);
                      setShowStickerModal(false);
                    }}
                  >
                    {emoji}
                    <StickerModalLabel>
                      {emoji}
                    </StickerModalLabel>
                  </StickerModalButton>
                ))}
              </StickerModalGrid>
            </ModalSection>

            <ModalSection>
              <SectionTitle>自定义贴纸</SectionTitle>
              <StickerModalButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  document.getElementById('modal-sticker-upload')?.click();
                }}
                style={{ 
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2))',
                  border: '2px dashed rgba(34, 197, 94, 0.5)'
                }}
              >
                📁
                <StickerModalLabel>上传自定义贴纸</StickerModalLabel>
              </StickerModalButton>
              <input
                id="modal-sticker-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    addCustomSticker(file);
                    setShowStickerModal(false);
                  }
                  // 清空input值，允许重复选择同一文件
                  e.target.value = '';
                }}
              />
            </ModalSection>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* <ControlsContainer>
        <ControlGroup>
          <ControlTitle>💾 保存图片</ControlTitle>
          <Button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={downloadImage}
          >
            下载图片
          </Button>
        </ControlGroup>
      </ControlsContainer> */}
    </CanvasContainer>
  );
}

export default CanvasEditor;
